import { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTheme } from 'next-themes';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { trackSelectPromotion, trackViewPromotion } from 'helpers/analytics/ga4Events';
import { fontStyleMap } from 'helpers/constants';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mobile } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';
import { GridProps } from './types';

const PromotionalCategoryGrid: FC<GridProps> = ({
  headlineText,
  isImageForHeadline,
  headLineImageSrc,
  headLineImageAltText,
  headlineTextColor,
  headlineTextColorDark,
  headlineTextSizeLg,
  headlineTextSizeSm,
  headlineTextStyle,
  subText,
  subTextColor,
  subTextColorDark,
  subTextSizeLg,
  subTextSizeSm,
  subTextStyle,
  gridBgColor,
  gridBgColorDark,
  isGridBgImage,
  gridBgimageSrc,
  gridBgimageAlt,
  ffidCurated,
  imageSrc,
  imageAlt,
  imageSrcMobile,
  imageAltMobile,
  target,
}) => {
  const { resolvedTheme = 'light' } = useTheme();
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMobileSize] = useMediaQuery(mobile);
  const [themeColor, setThemeColor] = useState('');

  // GA4: Promotion tracking
  const gridRef = useRef<HTMLDivElement>(null);
  const viewFired = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewFired.current) {
            trackViewPromotion({
              creativeName: imageAlt || ffidCurated || undefined,
              creativeSlot: 'promotional_category_grid',
              promotionId: `category_grid_${cleanString(ffidCurated ?? '')}`,
              promotionName: headlineText || undefined,
            });
            viewFired.current = true;
          }
        });
      },
      { threshold: 0.5 },
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, [imageAlt, ffidCurated, headlineText]);

  const handlePromotionClick = () => {
    trackSelectPromotion({
      creativeName: imageAlt || ffidCurated || undefined,
      creativeSlot: 'promotional_category_grid',
      promotionId: `category_grid_${cleanString(ffidCurated ?? '')}`,
      promotionName: headlineText || undefined,
    });
  };
  const headlineTextClassName = clsx([fontStyleMap[headlineTextStyle ?? 'regular'], 'leading-[1.2]']);
  const subTextClassName = clsx([fontStyleMap[subTextStyle ?? 'regular'], 'w-4/5']);
  
  let headlineFontSize = 20;
  if (isDesktopSize && headlineTextSizeLg) {
    headlineFontSize = headlineTextSizeLg;
  } else if (!isDesktopSize && headlineTextSizeSm) {
    headlineFontSize = headlineTextSizeSm;
  }
  let subTextFontSize = 18;
  if (isDesktopSize && subTextSizeLg) {
    subTextFontSize = subTextSizeLg;
  } else if (!isDesktopSize && subTextSizeSm) {
    subTextFontSize = subTextSizeSm;
  }
  const sizes = useImageSizes({
    sm: 0.3,
    md: 0.3,
    lg: 0.6,
    defaultSize: 0.6,
    maxWidth: 1060,
  });
  useEffect(() => {
    setThemeColor(resolvedTheme ?? '');
  }, [resolvedTheme]);

  return (
    <div className="mx-0 px-20 sm:mx-24 sm:px-0 md:mx-75 lg:mx-76 xl:mx-0">
      <div
        style={{
          ...(!isGridBgImage && {
            backgroundColor:
              themeColor === 'dark' ? (gridBgColorDark ?? gridBgColor ?? '#D2E6F2') : (gridBgColor ?? '#D2E6F2'),
          }),
        }}
        ref={gridRef}
        className="container w-full rounded-xl lg:rounded-2xl"
        data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-category-grid`}
      >
        {isGridBgImage && gridBgimageSrc && (
          <Image
            src={gridBgimageSrc}
            alt={gridBgimageAlt ?? ''}
            sizes="100vw"
            fill
            className="-z-5 object-cover object-center"
            data-ffid={`img-${cleanString(ffidCurated ?? '')}-grid-background`}
          />
        )}
        <div
          className="z-5 flex w-full flex-col gap-12 sm:gap-16 md:gap-24 p-16 pt-24 sm:p-24 md:px-34 md:py-32"
          data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-grid-content`}
        >
          {isImageForHeadline && headLineImageSrc ? (
            <div
              className="max-w-2/3 lg:max-w-1/2 mx-auto my-0 aspect-[5/1] max-h-60 lg:max-h-120"
              data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-headline-image`}
            >
              <Image
                src={headLineImageSrc}
                alt={headLineImageAltText ?? ''}
                sizes="50vw"
                fill
                className="object-fill"
                data-ffid={`img-${cleanString(ffidCurated ?? '')}-grid-headline`}
              />
            </div>
          ) : (
            headlineText && (
              <div className={`flex justify-center text-center`}>
                <h2
                  data-testid="headline-text"
                  style={{
                    color:
                      themeColor == 'dark' && headlineTextColorDark
                        ? headlineTextColorDark
                        : (headlineTextColor ?? '#333333'),
                    fontSize: headlineFontSize,
                  }}
                  className={headlineTextClassName}
                  data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-headline`}
                >
                  {headlineText.trim()}
                </h2>
              </div>
            )
          )}
          {subText && (
            <div className={`flex justify-center text-center`}>
              <h3
                data-testid="sub-text"
                style={{
                  color: themeColor == 'dark' && subTextColorDark ? subTextColorDark : (subTextColor ?? '#5C5C5C'),
                  fontSize: subTextFontSize,
                }}
                className={subTextClassName}
                data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-sub`}
              >
                {subText.trim()}
              </h3>
            </div>
          )}
          <Link
            link={target}
            title={imageAlt ?? ''}
            data-testid="grid-link"
            data-ffid={`btn-${cleanString(ffidCurated ?? '')}-slide`}
            className={`w-full block mt-4 ${isMobileSize ? 'aspect-[2.15/1]' : 'aspect-1'}`}
            onClick={handlePromotionClick}
          >
            <Image
              src={isMobileSize ? imageSrc : imageSrcMobile}
              alt={isMobileSize ? imageAlt : imageAltMobile}
              style={{ objectFit: 'cover' }}
              className="size-full rounded-xl object-cover lg:rounded-2xl"
              data-ffid={`img-${cleanString(ffidCurated ?? '')}-tile`}
              sizes={sizes}
              unoptimized={false}
            />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PromotionalCategoryGrid;
